import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { AuthService } from '..';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HTTP_HEADER_CONTENT_TYPE, MIME_TYPE_JSON, HTTP_HEADER_AUTHORIZATION, HTTP_HEADER_CONSOLIDATOR_CODE, HTTP_HEADER_MODULE_CODE, HTTP_TOKEN } from '../../utils/constant';
import { environment } from '../../../environments/environment';
import * as _ from 'lodash';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { LanguageService } from '../language/language.service';
import { DialogService } from '../dialog/dialog.service';
import { Router, ActivatedRoute } from '@angular/router';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { AwsAmplifyAwsv4Service, SignedRequestParams } from '../aws-amplify-awsv4/aws-amplify-awsv4.service';


@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private apiEndpoint = environment.apiEndpoint;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private dialogService:DialogService,
    private languageService: LanguageService,
    private router: Router,
    private amplifyService: AwsAmplifyAwsv4Service
  ) { }

  public get<T>(path, httpParams?, additionalHeaders?): Observable<T[] | T | any>{
    let result;
    this.authService.user.cognitoUser.getSession((err, session: CognitoUserSession) => {
      if (err) {
        result = of(err);
      }

      let url = new URL(path, this.apiEndpoint);
      let headers = this.setHeaders(session.getIdToken().getJwtToken(),additionalHeaders, true);

      result = this.http.get<T[] | T>(url.toString(), {
        headers: new HttpHeaders(headers),
        params: httpParams
      }).pipe(
        tap((response:any) => {
          if (response) {
            localStorage.setItem('token', response.token);
          }
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        })
      );
    });

    return result ? result : this.logoutSession();
  }

  public post<T>(path, data?, additionalHeaders?): Observable<T[] | T | any>{
    let result;
    this.authService.user.cognitoUser.getSession((err, session: CognitoUserSession) => {
      if (err) {
        result = of(err);
      }

      let url = Location.joinWithSlash(this.apiEndpoint, path);
      let headers = this.setHeaders(session.getIdToken().getJwtToken(), additionalHeaders, true);
      
      result = this.http.post<T[] | T>(url.toString(), data, {
        headers: new HttpHeaders(headers)
      }).pipe(
        tap((response:any) => {
          if (response) {
            localStorage.setItem('token', response.token);
          }
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        })
      )
      
    });
    return result ? result : this.logoutSession();

  }

  public put<T>(path, data?, additionalHeaders?): Observable<T[] | T | any>{
    let result;
    this.authService.user.cognitoUser.getSession((err, session: CognitoUserSession) => {
      if (err) {
        return result = of(err);
      }

      let url = Location.joinWithSlash(this.apiEndpoint, path);
      let headers = this.setHeaders(session.getIdToken().getJwtToken(),additionalHeaders, true);
      return result = this.http.put<T[] | T>(url.toString(), data, {
        headers: new HttpHeaders(headers)
      }).pipe(
        tap((response:any) => {
          if (response) {
            localStorage.setItem('token', response.token);
          }
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        })
      );
    });
    return result ? result : this.logoutSession();
  }

  public delete<T>(path, data?, additionalHeaders?): Observable<T[] | T | any>{
    let result;
    this.authService.user.cognitoUser.getSession((err, session: CognitoUserSession) => {
      if (err) {
        return result = of(err);
      }

      let url = Location.joinWithSlash(this.apiEndpoint, path);
      let headers = this.setHeaders(session.getIdToken().getJwtToken(), additionalHeaders, true);
      
      return result = this.http.delete<T[] | T>(url.toString(), {
        headers: new HttpHeaders(headers),
        body: data
      }).pipe(
        tap((response:any) => {
          if (response) {
            localStorage.setItem('token', response.token);
          }
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(error);
        })
      );;
    });
    return result ? result : this.logoutSession();
  }

  public getWithoutAuth<T>(path, httpParams?, additionalHeaders?): Observable<T[] | T | any>{
    
    const requestParms: SignedRequestParams = {
      api_endpoint: this.apiEndpoint,
      api_path: path,
      http_method: 'GET',
      aws_region: environment.region,
      aws_service: 'execute-api',
      data: null,
      params: httpParams
    }

    return this.amplifyService.generateSignedRequest(requestParms).pipe(
      switchMap(signedRequest => {
        return this.http.get<T[] | T>(signedRequest.url.toString(), {
          headers: new HttpHeaders(signedRequest.headers),
          params: signedRequest.params
        }).pipe(
          catchError((error) => {
            return throwError(error);
          })
        );
      })
    )

  }

  public postWithoutAuth<T>(path, data?, additionalHeaders?): Observable<T[] | T | any>{

    const requestParms: SignedRequestParams = {
      api_endpoint: this.apiEndpoint,
      api_path: path,
      http_method: 'POST',
      aws_region: environment.region,
      aws_service: 'execute-api',
      data: data,
      params: null
    }

    return this.amplifyService.generateSignedRequest(requestParms).pipe(
      switchMap(signedRequest => {
        return this.http.post<T[] | T>(signedRequest.url.toString(), signedRequest.data, {
          headers: new HttpHeaders(signedRequest.headers)
        }).pipe(
          catchError((error) => {
            return throwError(error);
          })
        );
      })
    )
    
  }

  private getModuleCode():string{
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    if(route.snapshot.data['moduleCode']){
      return route.snapshot.data['moduleCode']
    } else {
      return null;
    }
  }

  private setHeaders(sessionToken:any, additionalHeaders: any, useRequiredHeaders: boolean): any {
    let headers = {};
    let requiredHeaders = {};

    if(this.getModuleCode()){
      requiredHeaders = {
        [ HTTP_HEADER_CONTENT_TYPE ]: MIME_TYPE_JSON,
        [ HTTP_HEADER_AUTHORIZATION ]: sessionToken,
        [ HTTP_HEADER_CONSOLIDATOR_CODE ]: this.authService.selectedConsolidator,
        [HTTP_HEADER_MODULE_CODE]: this.getModuleCode(),
        [HTTP_TOKEN]: localStorage.getItem('token')
      }
      
    } else {
      requiredHeaders = {
        [ HTTP_HEADER_CONTENT_TYPE ]: MIME_TYPE_JSON,
        [ HTTP_HEADER_AUTHORIZATION ]: sessionToken,
      }
    }

    // Set Headers according to params
    if (useRequiredHeaders) {
      headers = _.assign({}, additionalHeaders, requiredHeaders);
    } else {
      headers = _.assign({}, additionalHeaders, {
        [HTTP_HEADER_CONTENT_TYPE]: MIME_TYPE_JSON
      });
    }

    return headers;
  }

  // generalized error messages pertaining access
  private handleError(response: any) {

    if (!response) {
      this.logoutSession();
      return;
    }

    let message = response.error?.value?.toLowerCase();

    switch (message) {
      case 'unauthorized':
        // Unauthorized -> mali token No Permission -> wrong mapping/no permission
        this.logoutSession('MESSAGE_ERROR_UNAUTHORIZED');
        break;
      
      default:
        if (response.error?.token) {
          localStorage.setItem('token', response.error?.token);
        }
        // we just return the same response here to allow services to handle errors
        return response;
    }
    
  }

  // handling for AWS session timeout. Different from system token. But reused for other logout needs.
  private logoutSession(message:string = 'MESSAGE_ERROR_SESSION_TIMEOUT') {
    this.dialogService.openSnackBar(this.languageService.getTranslation(message, null));
    this.authService.logout();
    this.router.navigate(['/login']);
  }

}
